/**
 * Turning "J.Williams" in a play description into a named human.
 *
 * WHY THIS IS THE RISKIEST FILE IN THE DATA LAYER
 * -----------------------------------------------
 * Play-by-play text carries no athlete ids. It carries abbreviated names:
 *
 *   "(14:55) J.Williams right tackle to PHI 32 for 3 yards (C.DeJean)."
 *
 * The product's whole promise is that a beginner can trust what it tells
 * them. Attaching the wrong face and the wrong career to a play is not a
 * cosmetic bug, it is telling someone a made-up fact about a real person.
 * So the rule here is absolute:
 *
 *   **Resolve a name only when exactly one athlete in THIS GAME'S boxscore
 *   can be that name, and nothing in the sentence contradicts it.
 *   Otherwise return null and let the caller show the plain text.**
 *
 * Never a nearest match, never the more famous of two, never the one who
 * touched the ball more. Two candidates means we do not know.
 *
 * WHY THE BOXSCORE AND NOT A ROSTER, AND WHAT THAT COSTS
 * ------------------------------------------------------
 * The candidate set is the boxscore of the same payload the play came
 * from, which is roughly 60 athletes rather than a league-wide 1,700. That
 * is the difference between a rare collision and a constant one, it costs
 * no extra request, and it cannot name someone who was not in the game.
 *
 * The cost is not merely that a player with no recorded stat goes
 * unresolved. It is sharper than that, and it is the whole reason the
 * club check below exists.
 *
 * Measured over the same 16 real completed games (2025 regular season
 * week 1), 423 of the 7,073 name-shaped tokens in play text, 6.0%, are
 * answered by no boxscore athlete at all. Who is missing is not random.
 * ESPN files boxscore athletes under passing, rushing, receiving,
 * fumbles, defensive, interceptions, kickReturns, puntReturns, kicking
 * and punting, and an offensive lineman appears in none of those. The
 * group of players that never has a stat line is therefore also the
 * group that is penalised most often.
 *
 * A name whose real owner is absent does not look absent. It looks
 * *unique*, because the only candidate the index holds is the other
 * person of that name:
 *
 *   "PENALTY on KC-J.Taylor, False Start, 5 yards ..."
 *
 * Jawaan Taylor, Kansas City's right tackle, took that penalty and
 * recorded no statistic. Ja'Sir Taylor of the Chargers played and did.
 * Counting candidates alone makes "J.Taylor" a confident, unique, wrong
 * answer, and puts a Chargers portrait under a sentence that says Kansas
 * City.
 *
 * THE DISPROOF IS IN THE SENTENCE
 * -------------------------------
 * The club is right there in the text as the "KC-" prefix, and on the
 * candidate as `teamId`. So where the text names a club, the candidate
 * has to belong to it, and a mismatch resolves to nobody.
 *
 * This is a veto and never a selection. It can only ever withdraw an
 * answer, never choose between two. That asymmetry is deliberate: if the
 * premise "the prefix names that athlete's own club" were ever wrong, a
 * veto costs a missing face, whereas using the prefix to *pick* one of
 * two candidates would cost a wrong human. Checked over those 16 games,
 * 245 prefixed names resolved to a single boxscore athlete, 240 agreed
 * with the prefix, and all 5 that disagreed were the fabrication above.
 * The veto has yet to be seen suppressing a correct answer.
 *
 * It is not a complete fix and does not pretend to be. A name with no
 * club prefix whose owner is absent from the boxscore can still resolve
 * to the wrong person; only a roster-wide candidate set would close that,
 * at the price of two extra 400KB requests per game. What the veto does
 * is remove the case where the text itself already told us we were wrong.
 *
 * MATCHING BY CANDIDATE, NOT BY TOKEN
 * -----------------------------------
 * The obvious approach is to find name-shaped tokens with a regular
 * expression and look each one up. It is wrong, because real surnames
 * survive into play text with their spaces and full stops intact:
 *
 *   "A.St. Brown"   Amon-Ra St. Brown
 *   "A.Van Ginkel"  Andrew Van Ginkel
 *   "H.To'oTo'o"    Henry To'oTo'o
 *
 * No pattern can tell where those end without already knowing the names.
 * So we go the other way: build the keys from the boxscore, then look for
 * those exact strings in the text. Same 16 games, 6,650 matches found,
 * against 7,073 name-shaped tokens for a regular expression that mangles
 * the multi-word surnames.
 *
 * (There is one job a token pattern can still do, and `firstNameToken`
 * below does it: telling us that a name is present and where it starts,
 * without trying to say whose it is.)
 *
 * WHAT THE NUMBERS LOOK LIKE
 * --------------------------
 * Of those 6,650 matches, 98.5% were a single candidate and 1.5% were two,
 * and the ambiguous ones resolve to null. Colliding keys turned up in 7 of
 * the 16 games, so this is an ordinary occurrence and not an edge case:
 *
 *   C.Williams   Caleb Williams and Chris Williams, 49 references in one game
 *   T.Etienne    Travis Etienne Jr. and Trevor Etienne, opposing backfields
 *   J.Phillips   Jordan Phillips and Jaelan Phillips
 *   D.Jones      Dre'Mont Jones and D.J. Jones
 *
 * `__fixtures__/espn-boxscore-401772828.json` is the real JAX at CAR game
 * that produced three of those collisions at once, and the tests use it.
 *
 * ESPN often separates a collision for us, by writing more of the first
 * name: "Ty.Johnson" against "Ta.Johnson". Those spellings are keys here
 * too; see `playerNameKeys` in espnPlayerNormalizer for why and for the
 * counts. A key of the one-letter form alone would not be found in the
 * text at exactly the moments the disambiguation had already been done.
 */

import { normalizePlayTextTeamId } from "./espnNormalizer";
import {
  normalizeBoxscorePlayers,
  playerNameKey,
  playerNameKeys,
} from "./espnPlayerNormalizer";

/**
 * A character that cannot sit immediately before a name without meaning we
 * have landed inside a longer word. Digits, spaces, brackets, commas,
 * hyphens and full stops are all fine: real text has "PHI-M.Epps" and
 * "35.PENALTY on ...".
 */
const isNameChar = (char) => /[A-Za-z'’]/.test(char);

/**
 * Trailing boundary. Hyphen counts as a name character here, and only
 * here, so that the key "T.Smith" cannot match inside "T.Smith-Wade".
 */
const isTrailingNameChar = (char) => /[A-Za-z'’-]/.test(char);

/**
 * The club prefix immediately before a name, as play text writes it:
 * "PENALTY on KC-J.Taylor", "recovered by IND-X.Howard", "downed by
 * NYJ-A.Beck".
 *
 * Anchored to the end of everything before the name, so it only ever
 * looks at the characters touching it. The leading boundary is what
 * keeps "Center-T.Sieg" and "Holder-B.Anger" out: the "ter" and "der"
 * are preceded by a letter, so they are not a prefix, and every real
 * club abbreviation is two or three letters standing alone.
 */
const TEAM_PREFIX = /(?:^|[^A-Za-z0-9])([A-Za-z]{2,3})-$/;

/**
 * The club the sentence attributes this name to, or null when it does
 * not say. Null is the common case and means "no constraint".
 */
function teamPrefixBefore(source, start) {
  const match = TEAM_PREFIX.exec(source.slice(0, start));
  return match ? normalizePlayTextTeamId(match[1]) : null;
}

/**
 * A name-shaped token: an initial block, a full stop, then a surname.
 *
 * This pattern is NOT used to read a name out of the text. The whole
 * argument above is that it cannot be: it would cut "A.St. Brown" down to
 * "A.St". It is used only to answer a narrower question that has no other
 * answer, and that `src/utils/playActor.js` depends on: *is there a name
 * here at all, and where does it start?* For that, the opening few
 * characters are enough and the ragged tail does not matter.
 *
 * The shape is read off real text rather than assumed. The initial block
 * is one to three letters because that is ESPN's entire observed
 * vocabulary. The surname must begin with a capital followed by a small
 * letter or an apostrophe, which admits "McKivitz", "St. Brown" and
 * "O'Donnell" while rejecting the run-on sentences ESPN really writes:
 * "... - No Play.PENALTY on ..." is not somebody called Play.
 *
 * The initial block allows a capital inside it, as in "AJ.Dillon", even
 * though ESPN wrote no such token in any of the 16 games. That is not
 * speculation about their style, it is what keeps this pattern in step
 * with the keys: a first name spelled "AJ" or "KaVontae" produces
 * lengthened keys spelled the same way. All 2,941 keys built across the
 * 16 games match this pattern, so a key can never be found somewhere the
 * pattern sees no name, which is the property playActor rests on.
 * Loosening it changed the first token found on 0 of the 2,914 real
 * plays, so it costs nothing in false positives.
 */
const NAME_TOKEN = /(?:^|[^A-Za-z'’])([A-Z][A-Za-z]{0,2}\.[A-Z][a-z'’])/;

/**
 * Where the first name-shaped token in a play description begins, or null.
 *
 * @returns {{ text: string, start: number }|null}
 */
export function firstNameToken(playText) {
  const source = String(playText ?? "");
  const match = NAME_TOKEN.exec(source);
  if (!match) return null;

  const start = match.index + match[0].length - match[1].length;
  return { text: match[1], start };
}

/**
 * The candidate set for one game.
 *
 * @param {object} gamePackage ESPN game package (`gamepackageJSON`)
 * @returns {{
 *   players: object[],
 *   byId: Map<string, object>,
 *   byNameKey: Map<string, object[]>,
 *   keys: string[],
 *   ambiguousKeys: string[],
 *   size: number
 * }}
 */
export function buildPlayerIndex(gamePackage, context = {}) {
  const players = normalizeBoxscorePlayers(gamePackage, context);

  const byId = new Map();
  const byNameKey = new Map();

  players.forEach((player) => {
    byId.set(player.providerPlayerId, player);

    // Every spelling the feed may use for them, not only the one-letter
    // one, so that "Ty.Johnson" is findable at all.
    (player.nameKeys ?? []).forEach((key) => {
      const bucket = byNameKey.get(key);
      if (bucket) bucket.push(player);
      else byNameKey.set(key, [player]);
    });
  });

  // Longest first, so "T.Smith-Wade" is tried before "T.Smith" and the
  // longer human wins the span.
  const keys = [...byNameKey.keys()].sort(
    (a, b) => b.length - a.length || a.localeCompare(b)
  );

  const ambiguousKeys = keys.filter((key) => byNameKey.get(key).length > 1);

  return {
    players,
    byId,
    byNameKey,
    keys,
    ambiguousKeys,
    size: players.length,
  };
}

/** An empty index, so a caller with no boxscore yet needs no special case. */
export function emptyPlayerIndex() {
  return buildPlayerIndex(null);
}

/**
 * The one athlete this key can mean, or null.
 *
 * Null covers both "nobody in this game is called that" and "two people in
 * this game are called that". The caller treats them the same way, which
 * is the point: in neither case do we know who made the play.
 *
 * A key on its own carries no club, so this cannot apply the team check.
 * `findPlayerMentions` is the entry point that can, because it has the
 * sentence around the name, and it is the one the feed uses.
 */
export function resolvePlayerName(index, nameKey) {
  const candidates = index?.byNameKey?.get(String(nameKey ?? "")) ?? [];
  return candidates.length === 1 ? candidates[0] : null;
}

/** True when a key names more than one athlete in this game. */
export function isAmbiguousName(index, nameKey) {
  const candidates = index?.byNameKey?.get(String(nameKey ?? "")) ?? [];
  return candidates.length > 1;
}

/**
 * Every place a boxscore athlete is named in one play description.
 *
 * Returns non-overlapping mentions in the order they appear. A mention we
 * will not attribute is still reported, with `player: null`, so a caller
 * can tell "we found a name we will not attribute" from "there was no
 * name here". Neither one may be rendered as a player.
 *
 * Two things withhold the player, and they are reported separately
 * because they are different facts about the sentence:
 *
 *   `ambiguous`     two athletes in this game answer to the name.
 *   `teamMismatch`  the sentence names a club and the only candidate
 *                   plays for a different one, so the real owner of the
 *                   name is somebody the boxscore does not contain.
 *
 * @returns {{ text: string, nameKey: string, start: number, end: number,
 *   player: object|null, ambiguous: boolean, teamId: string|null,
 *   teamMismatch: boolean }[]}
 */
export function findPlayerMentions(index, playText) {
  const source = String(playText ?? "");
  const keys = index?.keys ?? [];
  if (source === "" || keys.length === 0) return [];

  const found = [];

  keys.forEach((key) => {
    let from = 0;

    for (;;) {
      const start = source.indexOf(key, from);
      if (start === -1) break;
      from = start + 1;

      const end = start + key.length;
      const before = start > 0 ? source[start - 1] : "";
      const after = end < source.length ? source[end] : "";

      if (before && isNameChar(before)) continue;
      if (after && isTrailingNameChar(after)) continue;

      found.push({ key, start, end });
    }
  });

  // Earliest first; where two keys start together the longer one wins, and
  // anything overlapping an accepted span is dropped.
  found.sort((a, b) => a.start - b.start || b.end - a.end);

  const mentions = [];
  let claimedTo = 0;

  found.forEach(({ key, start, end }) => {
    if (start < claimedTo) return;
    claimedTo = end;

    const candidates = index.byNameKey.get(key) ?? [];
    const only = candidates.length === 1 ? candidates[0] : null;

    // The club named in the sentence, and the veto it can cast. Only a
    // candidate that knows its own club can be contradicted by one.
    const teamId = teamPrefixBefore(source, start);
    const teamMismatch = Boolean(
      only && teamId && only.teamId && only.teamId !== teamId
    );

    mentions.push({
      text: key,
      nameKey: key,
      start,
      end,
      player: teamMismatch ? null : only,
      ambiguous: candidates.length > 1,
      teamId,
      teamMismatch,
    });
  });

  return mentions;
}

/**
 * A play description split into renderable pieces.
 *
 * Only uniquely resolved names become `player` segments. A name that is
 * ambiguous, or that the sentence's own club prefix contradicts, stays
 * inside the surrounding text, which is exactly the "leave it as plain
 * text" behaviour the rule demands, and the caller does not have to
 * remember to check a flag to get it right.
 *
 * @returns {({ type: "text", text: string }
 *          | { type: "player", text: string, player: object })[]}
 */
export function annotatePlayText(index, playText) {
  const source = String(playText ?? "");
  if (source === "") return [];

  const mentions = findPlayerMentions(index, source).filter((m) => m.player);
  if (mentions.length === 0) return [{ type: "text", text: source }];

  const segments = [];
  let cursor = 0;

  mentions.forEach((mention) => {
    if (mention.start > cursor) {
      segments.push({ type: "text", text: source.slice(cursor, mention.start) });
    }
    segments.push({
      type: "player",
      text: source.slice(mention.start, mention.end),
      player: mention.player,
    });
    cursor = mention.end;
  });

  if (cursor < source.length) {
    segments.push({ type: "text", text: source.slice(cursor) });
  }

  return segments;
}

export { playerNameKey, playerNameKeys };
